import { call, put, takeLatest, select, cancelled } from 'redux-saga/effects';
import { TEXT_TO_SPEECH_VOICE, GENERATE_TEXT_TO_SPEECH } from '../constants';

const ELEVENLABS_API_TEXT_TO_SPEECH_URL = 'https://api.elevenlabs.io/v1';
const ELEVENLABS_API_VOICES_URL = 'https://api.elevenlabs.io/v2';
const OUTPUT_FORMAT = 'output_format=mp3_44100_128';
const MAX_CHUNK_SIZE = 4000; // Maximum chunk size

/**
 * Combines multiple audio blobs into one
 *
 * @param {Blob[]} blobs - Array of audio blobs to combine
 * @returns {Promise<Blob>} A promise that resolves to the combined audio blob
 */
function combineAudioBlobs(blobs) {
  return Promise.resolve(new Blob(blobs, { type: 'audio/mpeg' }));
}

/**
 * Splits text into chunks based on maximum size
 *
 * @param {string} text - The text to split into chunks
 * @param {number} maxChunkSize - Maximum character count per chunk
 * @returns {string[]} Array of text chunks
 */
function splitTextIntoChunks(text, maxChunkSize) {
  if (text.length <= maxChunkSize) {
    return [text];
  }

  const chunks = [];
  let currentPosition = 0;

  while (currentPosition < text.length) {
    let chunkEnd = Math.min(currentPosition + maxChunkSize, text.length);

    // Look for paragraph boundary
    const paragraphEnd = text.lastIndexOf('\n', chunkEnd);
    if (paragraphEnd > currentPosition) {
      chunkEnd = paragraphEnd + 1; // Include the newline character
    } else {
      // Look for the last space
      const spaceEnd = text.lastIndexOf(' ', chunkEnd);
      if (spaceEnd > currentPosition) {
        chunkEnd = spaceEnd + 1; // Include the space
      }
    }

    chunks.push(text.substring(currentPosition, chunkEnd).trim());
    currentPosition = chunkEnd;
  }

  return chunks;
}

/**
 * Fetches voice ID for text-to-speech service
 *
 * @generator
 * @yields {Object} Redux action with voice ID
 */
function* fetchTextToSpeechVoice() {
  const elevenlabs = yield select((state) => state.config.elevenlabs);
  const { apiKey, voice } = elevenlabs ?? {};
  try {
    const url = new URL(`${ELEVENLABS_API_VOICES_URL}/voices`);
    url.searchParams.append('include_total_count', 'true');
    url.searchParams.append('search', voice);
    const response = yield call(fetch, url, {
      method: 'get',
      headers: {
        'xi-api-key': apiKey,
      },
    });

    const data = yield call([response, 'json']);
    const voiceId = data?.voices?.[0]?.voice_id;
    if (!voiceId) return;
    yield put({ type: TEXT_TO_SPEECH_VOICE.SUCCESS, payload: { voiceId } });
  } catch (error) {
    return null;
  }
}

/**
 * Processes a single text fragment
 *
 * @generator
 * @param {string} text - Text to convert to speech
 * @param {string} voiceId - ElevenLabs voice ID
 * @param {string} apiKey - ElevenLabs API key
 * @param {Function} [callback=null] - Optional callback function for progress/results
 * @yields {Object} Response with audio blob or error
 * @returns {Object} Object containing audioBlob or error
 */
function* processChunk(text, voiceId, apiKey, callback = null) {
  try {
    const response = yield call(
      fetch,
      `${ELEVENLABS_API_TEXT_TO_SPEECH_URL}/text-to-speech/${voiceId}?${OUTPUT_FORMAT}`,
      {
        method: 'POST',
        headers: {
          Accept: 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey,
        },
        body: JSON.stringify({
          text,
          model_id: 'eleven_multilingual_v2',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
          },
        }),
      }
    );

    if (response.ok) {
      const audioBlob = yield call([response, 'blob']);
      if (callback) {
        const audioUrl = URL.createObjectURL(audioBlob);
        callback({ audioUrl });
      }
      return { audioBlob };
    }
    if (callback) {
      const data = yield call([response, 'json']);
      callback({ error: data?.detail?.message ?? 'Bad request' });
    }
  } catch (error) {
    callback?.({ error: error.message });
  } finally {
    // Check if the task was cancelled
    if (yield cancelled()) {
      callback?.({ cancelled: true });
    }
  }
}

/**
 * Generates speech from text using ElevenLabs API
 *
 * @generator
 * @param {Object} action - Redux action
 * @param {Object} action.payload - Action payload
 * @param {string} action.payload.text - Text to convert to speech
 * @param {Function} action.payload.callback - Callback function for results
 * @yields {Object} Audio processing results
 */
function* generateSpeech({ payload }) {
  const { text, callback } = payload;
  const elevenlabs = yield select((state) => state.config.elevenlabs);
  const { voiceId } = yield select((state) => state.textToSpeech);
  const apiKey = elevenlabs?.apiKey;

  if (!text?.trim() || !voiceId || !apiKey) {
    callback?.({ error: 'Bad request data' });
    return;
  }

  try {
    // Split text into parts based on the limit
    const textChunks = splitTextIntoChunks(text, MAX_CHUNK_SIZE);

    // If text fits into a single request, use normal logic
    if (textChunks.length === 1) {
      yield call(processChunk, textChunks[0], voiceId, apiKey, callback);
      return;
    }

    // Process multiple parts and combine results
    const audioBlobs = [];
    let hasError = false;

    // Use for...of for direct iteration through array elements
    for (const chunk of textChunks) {
      const result = yield call(processChunk, chunk, voiceId, apiKey);

      if (result.error) {
        hasError = true;
        callback?.({ error: result.error });
        break;
      }

      audioBlobs.push(result.audioBlob);
    }

    // If no errors, combine audio and return result
    if (!hasError && audioBlobs.length > 0) {
      const combinedBlob = yield call(combineAudioBlobs, audioBlobs);
      const audioUrl = URL.createObjectURL(combinedBlob);
      callback?.({ audioUrl });
    }
  } catch (error) {
    callback?.({ error: error.message });
  } finally {
    if (yield cancelled()) {
      callback?.({ cancelled: true });
    }
  }
}

/**
 * Root saga for text-to-speech functionality
 *
 * @generator
 * @yields {Object} Saga effects for handling text-to-speech actions
 */
export default function* textToSpeechSaga() {
  yield takeLatest(TEXT_TO_SPEECH_VOICE.REQUEST, fetchTextToSpeechVoice);
  yield takeLatest(GENERATE_TEXT_TO_SPEECH.REQUEST, generateSpeech);
}
